/**
 * Runsheet adapter (v2) — hits the redesigned eos-app endpoints.
 *
 *   POST /api/stream-keys/{streamKey}/runsheets       create
 *   GET  /api/stream-keys/{streamKey}/runsheets       list by streamKey
 *   GET  /api/runsheets/{runsheetId}                  full (rows + normalizedRows)
 *   PUT  /api/runsheets/{runsheetId}                  update in place
 *   DELETE /api/runsheets/{runsheetId}                soft delete
 *   POST /api/runsheets/normalize                     AI preview, no save
 *   GET  /api/streams/{streamId}/available-runsheets  list for project picker
 *   GET  /api/streams/{streamId}/runsheet-assignment  current assignment
 *   PUT  /api/streams/{streamId}/runsheet-assignment  project-page swap
 */

import {
  UseMutationResult,
  useMutation,
  UseQueryResult,
  useQuery,
  useQueryClient,
} from 'react-query';
import ApiV2 from './api-v2';

export interface RunsheetRowRaw {
  [key: string]: string | number | boolean | null | undefined;
}

export interface RunsheetSpeaker {
  name: string;
  email?: string;
}

export interface RunsheetRowNormalized {
  title: string;
  speakers: RunsheetSpeaker[];
  suggestedStartTime?: string;
  suggestedEndTime?: string;
}

export interface RunsheetSummary {
  runsheetId: string;
  streamKey: string;
  title: string;
  rowCount: number;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  createdBy?: string;
}

export interface Runsheet extends RunsheetSummary {
  rows: RunsheetRowRaw[];
  normalizedRows: RunsheetRowNormalized[];
}

export interface StreamRunsheetAssignment {
  streamId: string;
  streamKey: string;
  projectId: string;
  runsheetId: string;
  source: 'auto_latest' | 'project_override';
  assignedAt: string;
  processedAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

const streamKeyRunsheetsKey = (streamKey?: string) =>
  ['runsheets', 'by-stream-key', streamKey] as const;
const runsheetKey = (runsheetId?: string) => ['runsheet', runsheetId] as const;
const streamAvailableKey = (streamId?: string) =>
  ['runsheets', 'available-for-stream', streamId] as const;
const streamAssignmentKey = (streamId?: string) => ['runsheet-assignment', streamId] as const;

// ------------------------------------------------------------------
// Queries
// ------------------------------------------------------------------

/**
 * Single round-trip replacement for listing runsheets across many stream keys.
 * The server fans out the DynamoDB Queries in parallel, so this avoids the
 * browser's ~6 concurrent-request cap.
 */
export function useListRunsheetsBulk(
  streamKeys: string[] | undefined,
): UseQueryResult<RunsheetSummary[]> {
  const keys = streamKeys ?? [];
  const cacheKey = ['runsheets', 'bulk', [...keys].sort().join('|')] as const;
  return useQuery(
    cacheKey,
    async () => {
      if (keys.length === 0) return [];
      const response = await ApiV2.post<ApiResponse<{ runsheets: RunsheetSummary[] }>>(
        '/api/runsheets/bulk',
        { streamKeys: keys },
      );
      return response.data?.runsheets ?? [];
    },
    {
      enabled: keys.length > 0,
      refetchOnWindowFocus: false,
      // Keep the list warm for 30s so tab-switching back is instant.
      staleTime: 30_000,
    },
  );
}

export function useListRunsheetsByStreamKey(streamKey?: string): UseQueryResult<RunsheetSummary[]> {
  return useQuery(
    streamKeyRunsheetsKey(streamKey),
    async () => {
      if (!streamKey) return [];
      const encoded = encodeURIComponent(streamKey);
      const response = await ApiV2.get<
        ApiResponse<{ streamKey: string; runsheets: RunsheetSummary[] }>
      >(`/api/stream-keys/${encoded}/runsheets`);
      return response.data?.runsheets ?? [];
    },
    { enabled: !!streamKey, refetchOnWindowFocus: false },
  );
}

export function useGetRunsheet(runsheetId?: string): UseQueryResult<Runsheet | null> {
  return useQuery(
    runsheetKey(runsheetId),
    async () => {
      if (!runsheetId) return null;
      const response = await ApiV2.get<ApiResponse<{ runsheet: Runsheet }>>(
        `/api/runsheets/${encodeURIComponent(runsheetId)}`,
      );
      return response.data?.runsheet ?? null;
    },
    { enabled: !!runsheetId, refetchOnWindowFocus: false },
  );
}

export function useAvailableRunsheetsForStream(streamId?: string): UseQueryResult<{
  streamKey: string;
  currentRunsheetId: string;
  runsheets: RunsheetSummary[];
} | null> {
  return useQuery(
    streamAvailableKey(streamId),
    async () => {
      if (!streamId) return null;
      const response = await ApiV2.get<
        ApiResponse<{
          streamKey: string;
          currentRunsheetId: string;
          runsheets: RunsheetSummary[];
        }>
      >(`/api/streams/${encodeURIComponent(streamId)}/available-runsheets`);
      return response.data ?? null;
    },
    { enabled: !!streamId, refetchOnWindowFocus: false },
  );
}

export function useStreamRunsheetAssignment(
  streamId?: string,
): UseQueryResult<StreamRunsheetAssignment | null> {
  return useQuery(
    streamAssignmentKey(streamId),
    async () => {
      if (!streamId) return null;
      const response = await ApiV2.get<
        ApiResponse<{ assignment: StreamRunsheetAssignment | null }>
      >(`/api/streams/${encodeURIComponent(streamId)}/runsheet-assignment`);
      return response.data?.assignment ?? null;
    },
    { enabled: !!streamId, refetchOnWindowFocus: false },
  );
}

// ------------------------------------------------------------------
// Mutations
// ------------------------------------------------------------------

export function useNormalizeRunsheet(): UseMutationResult<
  { rows: RunsheetRowRaw[]; normalizedRows: RunsheetRowNormalized[] },
  unknown,
  { csvText: string }
> {
  return useMutation(async ({ csvText }) => {
    const response = await ApiV2.post<
      ApiResponse<{
        rows: RunsheetRowRaw[];
        normalizedRows: RunsheetRowNormalized[];
      }>
    >(`/api/runsheets/normalize`, { csvText });
    if (!response.success || !response.data) {
      throw new Error(response.error ?? 'Failed to normalize runsheet');
    }
    return response.data;
  });
}

export function useCreateRunsheet(): UseMutationResult<
  Runsheet,
  unknown,
  {
    streamKey: string;
    title: string;
    rows: RunsheetRowRaw[];
    normalizedRows: RunsheetRowNormalized[];
  }
> {
  const qc = useQueryClient();
  return useMutation(
    async ({ streamKey, title, rows, normalizedRows }) => {
      const encoded = encodeURIComponent(streamKey);
      const response = await ApiV2.post<ApiResponse<{ runsheet: Runsheet }>>(
        `/api/stream-keys/${encoded}/runsheets`,
        {
          title,
          rows,
          normalizedRows,
        },
      );
      if (!response.success || !response.data) {
        throw new Error(response.error ?? 'Failed to create runsheet');
      }
      return response.data.runsheet;
    },
    {
      onSuccess: (runsheet) => {
        // Invalidate every runsheet list cache (bulk + by-stream-key) so the
        // sidebar list refreshes without a page reload.
        void qc.invalidateQueries(['runsheets']);
        void qc.invalidateQueries(streamKeyRunsheetsKey(runsheet.streamKey));
      },
    },
  );
}

export function useUpdateRunsheet(): UseMutationResult<
  Runsheet,
  unknown,
  {
    runsheetId: string;
    title?: string;
    rows?: RunsheetRowRaw[];
    normalizedRows?: RunsheetRowNormalized[];
  }
> {
  const qc = useQueryClient();
  return useMutation(
    async ({ runsheetId, title, rows, normalizedRows }) => {
      const body: Record<string, unknown> = {};
      if (title !== undefined) body.title = title;
      if (rows !== undefined) body.rows = rows;
      if (normalizedRows !== undefined) body.normalizedRows = normalizedRows;
      const response = await ApiV2.put<ApiResponse<{ runsheet: Runsheet }>>(
        `/api/runsheets/${encodeURIComponent(runsheetId)}`,
        body,
      );
      if (!response.success || !response.data) {
        throw new Error(response.error ?? 'Failed to update runsheet');
      }
      return response.data.runsheet;
    },
    {
      onSuccess: (runsheet) => {
        void qc.invalidateQueries(runsheetKey(runsheet.runsheetId));
        void qc.invalidateQueries(['runsheets']);
      },
    },
  );
}

export function useDeleteRunsheet(): UseMutationResult<
  { runsheetId: string },
  unknown,
  { runsheetId: string; streamKey?: string }
> {
  const qc = useQueryClient();
  return useMutation(
    async ({ runsheetId }) => {
      const response = await ApiV2.delete<ApiResponse<{ runsheetId: string }>>(
        `/api/runsheets/${encodeURIComponent(runsheetId)}`,
      );
      if (!response.success || !response.data) {
        throw new Error(response.error ?? 'Failed to delete runsheet');
      }
      return response.data;
    },
    {
      onSuccess: (_data, variables) => {
        void qc.invalidateQueries(runsheetKey(variables.runsheetId));
        // Blanket-invalidate every runsheet list query so the deleted row
        // disappears from the list page immediately.
        void qc.invalidateQueries(['runsheets']);
      },
    },
  );
}

export interface RunsheetAssignmentRenameResult {
  totalFragments: number;
  runsheetRowCount: number;
  renamedCount: number;
}

export interface RunsheetAssignmentDdbRenameResult {
  totalSegments: number;
  runsheetRowCount: number;
  renamedCount: number;
}

export interface SetStreamRunsheetAssignmentResult {
  assignment: StreamRunsheetAssignment;
  rename: RunsheetAssignmentRenameResult;
  ddbRename: RunsheetAssignmentDdbRenameResult;
}

export function useSetStreamRunsheetAssignment(): UseMutationResult<
  SetStreamRunsheetAssignmentResult,
  unknown,
  { streamId: string; runsheetId: string }
> {
  const qc = useQueryClient();
  return useMutation(
    async ({ streamId, runsheetId }) => {
      const response = await ApiV2.put<
        ApiResponse<{
          assignment: StreamRunsheetAssignment;
          rename: RunsheetAssignmentRenameResult;
          ddbRename: RunsheetAssignmentDdbRenameResult;
        }>
      >(`/api/streams/${encodeURIComponent(streamId)}/runsheet-assignment`, {
        runsheetId,
      });
      if (!response.success || !response.data) {
        throw new Error(response.error ?? 'Failed to swap runsheet');
      }
      return response.data;
    },
    {
      onSuccess: ({ assignment }) => {
        void qc.invalidateQueries(streamAssignmentKey(assignment.streamId));
        void qc.invalidateQueries(streamAvailableKey(assignment.streamId));
      },
    },
  );
}
